import { ChangeDetectionStrategy, Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import { ITask2ReportItem } from '../fi-task-v2-report';
import { FiTaskV2Service } from '@fi-app/task-v2/fi-task-v2.service';
import { ITaskV2, ITaskV2ChecklistDataBase, ITaskV2Log, ITaskV2Overview } from '@fi-app/task-v2/fi-task-v2';
import { BehaviorSubject, filter, Subject, takeUntil } from 'rxjs';
import { FiTaskAssignmentType, FiTaskType } from '@fi-app/task-v2/fi-task-v2-enums';
import { FiTaskV2HelperService } from '@fi-app/task-v2/fi-task-v2-helper.service';
import { ICommentModel } from '@fi-app/shared/models';
import { IDateByItem } from '@fi-shared/models/models';
import {
    ACTIONS_TYPES,
    TaskActionsBuilderService,
} from '../fi-task-v2-report-widget-details/widget-details-content/widget-details-table/task-actions.builder.service';
import { FiTaskV2ReportHelperService } from '../services/fi-task-v2-report-helper.service';
import { FiAttachmentDisplaySize } from '@fi-shared/types';
import { AppTextService } from '@fi-shared/app-text';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiTaskStatusType } from '@fi-app/task-v2/fi-task-v2-enums';
import { FiTaskStatusesConfig } from '@fi-app/task-v2/fi-task-v2-consts';

interface IFiTaskV2ReportItemOverviewDialogData {
    taskReport$: BehaviorSubject<ITask2ReportItem>;
    actionExecuted$: Subject<{ type: ACTIONS_TYPES; item: ITask2ReportItem }>;
}

@Component({
    selector: 'fi-task-v2-report-item-overview-dialog',
    templateUrl: './fi-task-v2-report-item-overview-dialog.component.html',
    styleUrl: './fi-task-v2-report-item-overview-dialog.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-task-v2-report-item-overview-dialog' },
    standalone: false,
})
export class FiTaskV2ReportItemOverviewDialogComponent
    extends FiDialogsContentBase<IFiTaskV2ReportItemOverviewDialogData, void>
    implements OnInit
{
    protected task: ITaskV2;
    protected lastReminder: IDateByItem;
    protected comments: ICommentModel[];
    protected latestTaskLogs: ITaskV2Log[];

    protected loading$ = new BehaviorSubject<boolean>(true);

    private fiTaskV2Service = inject(FiTaskV2Service);
    private fiTaskV2HelperService = inject(FiTaskV2HelperService);
    private fiTaskV2ReportHelperService = inject(FiTaskV2ReportHelperService);
    private fiTaskActionsBuilderService = inject(TaskActionsBuilderService);
    private appTextService = inject(AppTextService);
    private fiStateService = inject(FiStateService);
    protected taskReport: ITask2ReportItem;
    protected readonly FiTaskStatusesConfig = FiTaskStatusesConfig;
    protected readonly FiAttachmentDisplaySize = FiAttachmentDisplaySize;

    private get checklistData(): ITaskV2ChecklistDataBase {
        return this.taskReport.AdditionalData?.ChecklistData;
    }

    public ngOnInit(): void {
        this.subscribeOnTaskReportChange();
        this.subscribeOnTaskChange();
        this.subscribeOnStateChange();
    }

    private isFirstLoad = true;
    private subscribeOnTaskReportChange(): void {
        this.data.taskReport$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((taskReport) => {
            this.taskReport = taskReport;
            this.setTaskData();
        });
    }

    private subscribeOnTaskChange(): void {
        // UPDATE_STATUS action is not relevant for task change (it is relevant for task report change and handled in WidgetDetailsTableComponent )
        this.data.actionExecuted$
            .pipe(
                takeUntil(this.ngOnDestroy$),
                filter((action) => action.type !== ACTIONS_TYPES.UPDATE_STATUS),
            )
            .subscribe(() => {
                this.setTaskData();
            });
    }

    private subscribeOnStateChange(): void {
        this.fiStateService.onFiStateGo$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.fiTaskV2ReportHelperService.setCurrentTaskItem(this.taskReport);
            if (!this.isFirstLoad) {
                this.closeDialog();
            }
        });
    }

    protected isDeadlineOverdue(): boolean {
        return this.task.IsDeadlinePassed && this.task.Status !== FiTaskStatusType.Completed;
    }

    protected isCompletedAfterDeadline(): boolean {
        return this.fiTaskV2ReportHelperService.isTaskCompletedAfterDeadline(this.task);
    }

    protected get isAutomatedTask(): boolean {
        return this.taskReport.TaskType === FiTaskType.ChecklistAutomated;
    }

    protected get isOneTimeTask(): boolean {
        return this.taskReport.TaskType === FiTaskType.ChecklistOneTime;
    }

    protected getBannerText(): string {
        return this.fiTaskV2ReportHelperService.getTaskReportOverviewBannerText(this.checklistData);
    }

    protected getBannerButtonText(): string {
        return this.fiTaskV2ReportHelperService.getTaskReportOverviewBannerButtonText(
            this.checklistData?.HasViewAccessToChecklist ?? false,
            this.checklistData?.IsChecklistAudit ?? false,
        );
    }

    protected onBannerButtonClick(): void {
        if (this.fiTaskV2HelperService.canOpenTaskChecklistView(this.checklistData)) {
            this.fiTaskV2HelperService.openTaskChecklistView(this.checklistData);
        }
        this.closeDialog();
    }

    protected showNoAssignedUsersText(): boolean {
        if (this.task.AssignedUsers.length) {
            return false;
        }
        if (this.task.AssignmentType === FiTaskAssignmentType.RolesUnits) {
            const coreFilter = this.task.AccessRightFilterCollection.AccessRightCoreFilter;
            return coreFilter.Roles.length > 0 && coreFilter.BusinessUnits.length > 0;
        }
        if (this.task.AssignmentType === FiTaskAssignmentType.Users) {
            const userFilter = this.task.AccessRightFilterCollection.AccessRightUserFilter;
            return userFilter.Users.length > 0;
        }

        // for any future changes (for example assign to clusters/areas etc)
        return false;
    }

    protected getNoAssignedUsersText() {
        const texts: string[] = [
            this.appTextService.get(
                this.task.AccessToPreferredBusinessUnitMode
                    ? 'task.no_assignees_current_unit'
                    : 'task.no_assignees_all_units',
            ),
        ];

        if (this.task?.TaskType === FiTaskType.ChecklistAutomated) {
            texts.push(this.appTextService.get('task.no_assignees_sharing_settings_description'));
        }

        return texts.join('<br>');
    }

    protected closeDialog(): void {
        this.dialogRef.close();
    }

    private setTaskData(): void {
        if (this.isFirstLoad) {
            this.loading$.next(true);
        }

        this.setTaskOverview()
            .catch((error) => {
                console.error('Error loading task data:', error);
                this.closeDialog();
            })
            .finally(() => {
                this.loading$.next(false);
                this.isFirstLoad = false;
            });
    }

    private setTaskOverview(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.fiTaskV2Service.getTaskOverviewNoTracking(this.taskReport.Id).then((result: ITaskV2Overview) => {
                this.task = result.Task;
                this.lastReminder = result.LastReminder;
                this.comments = result.Comments;
                this.latestTaskLogs = result.LatestTaskLogs;
                resolve();
            }, reject);
        });
    }

    protected canRestoreTask(): boolean {
        return this.fiTaskActionsBuilderService.canRestoreDeleteTask(this.task);
    }

    protected restoreTask(): void {
        this.fiTaskV2Service.saveTaskArchived([this.task], false).then(() => {
            this.fiTaskV2HelperService.showTaskRestoredSnackbar(this.task.Name);
            this.setTaskData();
        });
    }

    protected isTaskDescriptionCardExpanded(): boolean {
        return !!this.task.Description?.length || !!this.task.MediaBankFileReferences?.length;
    }

    protected visibleColumnsCount: number = 0;
    protected onVisibleColumnsChanged(visibleColumnsCount: number): void {
        this.visibleColumnsCount = visibleColumnsCount;
    }

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
